import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import { getAccountingDocumentMovement } from '@/modules/accounting/accountingDocumentMovement'
import { getStoredDocumentType } from '@/modules/file-reader'
import { supabase } from '@/lib/integrations/supabase'
import type { StoredDocument } from '@/modules/documents'
import {
  confirmOutgoingBookkeeping,
  hasBlockingBookkeepingWarnings,
  prepareOutgoingBookkeeping,
} from './accountingBookkeepingService'
import {
  confirmIncomingBookkeeping,
  hasBlockingIncomingWarnings,
  prepareIncomingBookkeeping,
} from './incomingBookkeepingService'

export interface BulkBookkeepingProgress {
  current: number
  total: number
  documentName: string
}

export interface BulkBookkeepingFailure {
  documentId: string
  documentName: string
  code: string
}

export interface BulkBookkeepingResult {
  total: number
  booked: number
  failed: number
  failures: BulkBookkeepingFailure[]
}

export interface PendingBookkeepingDocuments {
  documents: StoredDocument[]
  incoming: number
  outgoing: number
}

function failureCode(error: unknown) {
  const record = error && typeof error === 'object' ? error as Record<string, unknown> : null
  const message = String(record?.message || (error instanceof Error ? error.message : '')).trim()
  const code = String(record?.code || '').trim()
  if (code === 'PGRST202' || message.includes('bookkeep_accounting_incoming_document')) {
    return 'accounting_bookkeeping_incoming_migration_required'
  }
  return message || code || 'accounting_bookkeeping_bulk_failed'
}

function firstBlockingIncomingWarning(warnings: string[]) {
  const warning = warnings.find((value) => (
    ['supplier_not_in_memory', 'product_not_in_memory', 'operation_not_in_memory', 'bookkeeping_cfop_missing']
      .some((code) => value.startsWith(code))
    || value.startsWith('missing_required:')
  ))
  if (!warning) return 'accounting_bookkeeping_memory_match_required'
  if (warning.startsWith('missing_required:')) return 'accounting_bookkeeping_required_fields_missing'
  return warning.split(':')[0]
}

export async function listPendingBookkeepingDocuments(
  client: AccountingClient,
): Promise<PendingBookkeepingDocuments> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('unit_id', client.client_unit_id)
    .eq('document_kind', 'xml')
    .eq('status', 'valid')
    .not('storage_path', 'is', null)
    .order('created_at', { ascending: true })

  if (error) throw error

  let incoming = 0
  let outgoing = 0
  const documents = ((data || []) as StoredDocument[]).filter((document) => {
    const direction = getAccountingDocumentMovement(document, client.cnpj)
    if (direction === 'outgoing') {
      outgoing += 1
      return true
    }
    if (direction === 'incoming' && getStoredDocumentType(document) === 'nfe') {
      incoming += 1
      return true
    }
    return false
  })

  return { documents, incoming, outgoing }
}

export async function bookkeepAllPendingDocuments(
  officeUnitId: string,
  client: AccountingClient,
  documents: StoredDocument[],
  onProgress?: (progress: BulkBookkeepingProgress) => void,
): Promise<BulkBookkeepingResult> {
  const failures: BulkBookkeepingFailure[] = []
  let booked = 0

  for (let index = 0; index < documents.length; index += 1) {
    const document = documents[index]
    onProgress?.({
      current: index + 1,
      total: documents.length,
      documentName: document.original_name,
    })

    try {
      const direction = getAccountingDocumentMovement(document, client.cnpj)
      if (direction === 'incoming') {
        const preview = await prepareIncomingBookkeeping(officeUnitId, client, document)
        if (hasBlockingIncomingWarnings(preview.warnings)) {
          throw new Error(firstBlockingIncomingWarning(preview.warnings))
        }
        if (preview.confidence < 70) {
          throw new Error('accounting_bookkeeping_memory_confidence_required')
        }
        await confirmIncomingBookkeeping(preview)
      } else if (direction === 'outgoing') {
        const preview = await prepareOutgoingBookkeeping(officeUnitId, client, document)
        if (hasBlockingBookkeepingWarnings(preview.warnings)) {
          throw new Error('accounting_bookkeeping_required_fields_missing')
        }
        await confirmOutgoingBookkeeping(preview)
      } else {
        throw new Error('accounting_bookkeeping_direction_required')
      }
      booked += 1
    } catch (error) {
      failures.push({
        documentId: document.id,
        documentName: document.original_name,
        code: failureCode(error),
      })
    }
  }

  return {
    total: documents.length,
    booked,
    failed: failures.length,
    failures,
  }
}
